import {
  ArrowLeft,
  ArrowRight,
  BookmarkPlus,
  CalendarClock,
  CheckCircle2,
  FileDown,
  Loader2,
  Send,
  TestTube2,
  TriangleAlert,
} from "lucide-react";
import { useDataProvider, useGetList, useGetOne, useNotify } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { GulaClient } from "../gulaTypes";
import { useAgents } from "../useAgents";
import { CampaignResults } from "./CampaignResults";
import { DeleteCampaignButton } from "./DeleteCampaignButton";
import {
  EMAIL_PATH,
  SEGMENTS,
  emailProblems,
  sendCampaign,
  sendTestEmail,
  withIds,
  type EmailCampaign,
  type EmailContent,
  type EmailTemplate,
  type Segment,
} from "./email";
import { EmailContentEditor } from "./EmailContentEditor";
import {
  RecipientPicker,
  emptyFilters,
  selectedRecipientIds,
  type RecipientFilters,
} from "./RecipientPicker";
import { SAVE_LABEL, useAutosave } from "./useAutosave";

type Step = "content" | "people" | "send";
const STEPS: { key: Step; label: string }[] = [
  { key: "content", label: "1 · Contenido" },
  { key: "people", label: "2 · A quién" },
  { key: "send", label: "3 · Enviar" },
];

/** Local date-time for <input type="datetime-local">, one hour from now. */
const inAnHour = () => {
  const date = new Date(Date.now() + 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export const useEmailAudience = () => {
  const { data: clients = [], isPending } = useGetList<GulaClient>(
    "gula_clients_summary",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: unsubscribes = [] } = useGetList<{ id: string; email: string }>(
    "gula_email_unsubscribes",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const unsubscribed = useMemo(
    () => new Set(unsubscribes.map((row) => row.email.toLowerCase())),
    [unsubscribes],
  );
  return { clients, unsubscribed, isPending };
};

/** Load a saved template into the campaign, or save the campaign as one. */
const TemplateBar = ({
  segment,
  content,
  onLoad,
}: {
  segment: Segment;
  content: EmailContent;
  onLoad: (template: EmailTemplate) => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { meId } = useAgents();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [askName, setAskName] = useState(false);
  const { data: templates = [], refetch } = useGetList<EmailTemplate>(
    "gula_email_templates",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "updated_at", order: "DESC" },
    },
  );
  const sorted = [
    ...templates.filter((item) => item.audience === segment),
    ...templates.filter((item) => item.audience === "all"),
    ...templates.filter(
      (item) => item.audience !== segment && item.audience !== "all",
    ),
  ];

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await dataProvider.create("gula_email_templates", {
        data: {
          name: name.trim(),
          audience: segment,
          subject: content.subject,
          preheader: content.preheader ?? "",
          style: content.style,
          blocks: content.blocks,
          created_by: meId ?? null,
        },
      });
      notify("Plantilla guardada", { type: "success" });
      setAskName(false);
      setName("");
      refetch();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar la plantilla", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed p-3">
      <FileDown className="h-4 w-4 text-muted-foreground" />
      <select
        value=""
        onChange={(event) => {
          const template = templates.find(
            (item) => String(item.id) === event.target.value,
          );
          if (template) onLoad(template);
        }}
        className="h-9 min-w-56 rounded-md border bg-background px-2 text-sm"
      >
        <option value="">
          {templates.length
            ? "Empezar desde una plantilla…"
            : "Aún no hay plantillas"}
        </option>
        {sorted.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
            {template.audience === "clients"
              ? " (clientes)"
              : template.audience === "leads"
                ? " (por convertir)"
                : ""}
          </option>
        ))}
      </select>
      <div className="ml-auto flex items-center gap-2">
        {askName ? (
          <>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && save()}
              placeholder="Nombre de la plantilla"
              className="h-9 w-56"
            />
            <Button size="sm" onClick={save} disabled={saving || !name.trim()}>
              Guardar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAskName(false)}>
              Cancelar
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setAskName(true)}>
            <BookmarkPlus className="h-4 w-4" />
            Guardar como plantilla
          </Button>
        )}
      </div>
    </div>
  );
};

/** Draft campaign: content, recipients and send/schedule. */
const CampaignDraft = ({
  campaign,
  onSent,
}: {
  campaign: EmailCampaign;
  onSent: () => void;
}) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { agents } = useAgents();
  const { clients, unsubscribed, isPending } = useEmailAudience();
  const [step, setStep] = useState<Step>("content");
  const [name, setName] = useState(campaign.name);
  const [segment, setSegment] = useState<Segment>(campaign.segment);
  const [content, setContent] = useState<EmailContent>({
    subject: campaign.subject,
    preheader: campaign.preheader,
    style: campaign.style,
    blocks: withIds(campaign.blocks),
  });
  const [filters, setFilters] = useState<RecipientFilters>(emptyFilters());
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [when, setWhen] = useState<"now" | "later">("now");
  const [scheduledAt, setScheduledAt] = useState(inAnHour());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<"test" | "send" | null>(null);

  const saveState = useAutosave(
    "gula_email_campaigns",
    campaign.id,
    {
      name: name.trim() || "Sin nombre",
      segment,
      subject: content.subject,
      preheader: content.preheader ?? "",
      style: content.style,
      blocks: content.blocks,
    },
    true,
  );

  const recipientIds = selectedRecipientIds(
    clients,
    unsubscribed,
    segment,
    filters,
    excluded,
  );
  const problems = emailProblems(content);
  const { data: settings } = useGetOne<{ id: number; hourly_limit: number }>(
    "gula_email_settings",
    { id: 1 },
  );
  const perHour = settings?.hourly_limit ?? 90;
  const minutes = Math.ceil((recipientIds.length / perHour) * 60);
  const sample =
    clients.find((client) => String(client.id) === String(recipientIds[0]))
      ?.first_name ?? "María";

  const test = async () => {
    setBusy("test");
    try {
      const result = await sendTestEmail(content);
      notify(`Prueba enviada a ${result.to}`, { type: "success" });
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      const result = await sendCampaign(
        campaign.id,
        recipientIds,
        when === "later" ? new Date(scheduledAt).toISOString() : null,
      );
      notify(
        result.scheduled_at
          ? `Programada: ${result.queued} correos`
          : `Enviando ${result.queued} correos`,
        { type: "success" },
      );
      setConfirming(false);
      onSent();
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-5 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon" className="rounded-full">
          <Link to={EMAIL_PATH} title="Volver a Correo">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="h-10 max-w-md border-transparent px-2 text-2xl font-semibold shadow-none hover:border-input focus-visible:border-input md:text-2xl"
        />
        <span
          className={cn(
            "text-sm text-muted-foreground",
            saveState === "error" && "text-destructive",
          )}
        >
          {SAVE_LABEL[saveState]}
        </span>
        <span className="ml-auto rounded-full bg-muted px-3 py-1 text-sm">
          Para: {SEGMENTS.find((item) => item.value === segment)?.label}
        </span>
        <DeleteCampaignButton
          campaign={{ ...campaign, name: name.trim() || campaign.name }}
          onDeleted={() => navigate(EMAIL_PATH)}
        />
      </div>

      <div className="flex gap-1 rounded-full bg-muted p-1 sm:w-fit">
        {STEPS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setStep(item.key)}
            className={cn(
              "flex-1 rounded-full px-4 py-1.5 text-sm font-medium transition-colors sm:flex-none",
              step === item.key
                ? "bg-background shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {step === "content" ? (
        <>
          <TemplateBar
            segment={segment}
            content={content}
            onLoad={(template) => {
              setContent({
                subject: template.subject,
                preheader: template.preheader,
                style: template.style,
                blocks: withIds(
                  template.blocks.map((block) => ({ ...block, id: undefined })),
                ),
              });
              notify(`Plantilla «${template.name}» cargada`, { type: "info" });
            }}
          />
          <EmailContentEditor
            value={content}
            onChange={setContent}
            previewName={sample}
          />
          <div className="flex justify-end">
            <Button onClick={() => setStep("people")}>
              Elegir a quién
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      ) : null}

      {step === "people" ? (
        <>
          {isPending ? (
            <p className="text-sm text-muted-foreground">Cargando leads…</p>
          ) : (
            <RecipientPicker
              clients={clients}
              unsubscribed={unsubscribed}
              agents={agents}
              segment={segment}
              onSegmentChange={setSegment}
              filters={filters}
              onFiltersChange={setFilters}
              excluded={excluded}
              onExcludedChange={setExcluded}
            />
          )}
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep("content")}>
              <ArrowLeft className="h-4 w-4" />
              Contenido
            </Button>
            <Button
              onClick={() => setStep("send")}
              disabled={!recipientIds.length}
            >
              Revisar y enviar
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      ) : null}

      {step === "send" ? (
        <div className="grid max-w-3xl grid-cols-1 gap-4">
          <section className="flex flex-col gap-3 rounded-2xl border p-5">
            <h2 className="text-lg font-semibold">Resumen</h2>
            <dl className="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Asunto</dt>
              <dd className="font-medium">{content.subject || "—"}</dd>
              <dt className="text-muted-foreground">Para</dt>
              <dd>
                <strong>{recipientIds.length}</strong>{" "}
                {SEGMENTS.find(
                  (item) => item.value === segment,
                )?.label.toLowerCase()}{" "}
                <button
                  type="button"
                  className="text-muted-foreground hover:underline"
                  onClick={() => setStep("people")}
                >
                  (cambiar)
                </button>
              </dd>
              <dt className="text-muted-foreground">Desde</dt>
              <dd>Gula de Agua &lt;notificaciones@guladeagua.es&gt;</dd>
              <dt className="text-muted-foreground">Velocidad</dt>
              <dd>
                {perHour} correos por hora (límite del buzón de IONOS):{" "}
                {recipientIds.length <= perHour / 60
                  ? "sale en un momento"
                  : `tardará unos ${minutes < 60 ? `${minutes} min` : `${(minutes / 60).toFixed(1).replace(".", ",")} h`}`}
              </dd>
            </dl>
            {problems.length ? (
              <div className="flex flex-col gap-1 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
                {problems.map((problem) => (
                  <span key={problem} className="flex items-center gap-2">
                    <TriangleAlert className="h-4 w-4" />
                    {problem}
                  </span>
                ))}
              </div>
            ) : (
              <span className="flex items-center gap-2 text-sm text-lime-700 dark:text-lime-300">
                <CheckCircle2 className="h-4 w-4" />
                Lleva el enlace de baja y el pie legal automáticamente.
              </span>
            )}
          </section>

          <section className="flex flex-wrap items-center gap-3 rounded-2xl border p-5">
            <div className="flex-1">
              <h2 className="font-semibold">Mándate una prueba</h2>
              <p className="text-sm text-muted-foreground">
                Llega a tu correo con tu nombre para ver cómo queda en el móvil.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={test}
              disabled={busy !== null || problems.length > 0}
            >
              {busy === "test" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube2 className="h-4 w-4" />
              )}
              Enviarme una prueba
            </Button>
          </section>

          <section className="flex flex-col gap-3 rounded-2xl border p-5">
            <h2 className="font-semibold">¿Cuándo?</h2>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["now", "Ahora", Send],
                  ["later", "Programar", CalendarClock],
                ] as const
              ).map(([key, label, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setWhen(key)}
                  className={cn(
                    "flex items-center gap-2 rounded-full border px-4 py-2 text-sm",
                    when === key
                      ? "border-foreground bg-foreground text-background"
                      : "hover:bg-muted",
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
              {when === "later" ? (
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(event) => setScheduledAt(event.target.value)}
                  className="w-56"
                />
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">
              Mejor entre semana por la mañana (9–11 h) o a mediodía. Evita
              mandar dos correos a la misma persona la misma semana.
            </p>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep("people")}>
                <ArrowLeft className="h-4 w-4" />A quién
              </Button>
              <Button
                className="bg-lime-400 text-neutral-900 hover:bg-lime-300"
                disabled={
                  busy !== null || problems.length > 0 || !recipientIds.length
                }
                onClick={() => setConfirming(true)}
              >
                <Send className="h-4 w-4" />
                {when === "later" ? "Programar envío" : "Enviar ahora"}
              </Button>
            </div>
          </section>
        </div>
      ) : null}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {when === "later" ? "¿Programar el envío?" : "¿Enviar ya?"}
            </DialogTitle>
            <DialogDescription>
              «{content.subject}» a {recipientIds.length} personas
              {when === "later"
                ? ` el ${new Date(scheduledAt).toLocaleString("es-ES", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })}`
                : ""}
              . Una vez enviado no se puede deshacer (sí parar lo que falte).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-lime-400 text-neutral-900 hover:bg-lime-300"
              onClick={send}
              disabled={busy !== null}
            >
              {busy === "send" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Sí, {when === "later" ? "programar" : "enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** /correo/:id — the draft editor, or the results once it has been sent. */
export const CampaignPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: campaign,
    isPending,
    isFetchedAfterMount,
    refetch,
  } = useGetOne<EmailCampaign>(
    "gula_email_campaigns_summary",
    { id: Number(id) },
    {
      // Always the fresh row: the draft editor autosaves
      refetchOnMount: "always",
      staleTime: 0,
      refetchOnWindowFocus: false,
      refetchInterval: (query) =>
        ["sending", "scheduled"].includes(
          (query.state.data as EmailCampaign | undefined)?.status ?? "",
        )
          ? 10_000
          : false,
    },
  );
  useEffect(() => {
    if (isFetchedAfterMount && !campaign) navigate(EMAIL_PATH);
  }, [campaign, isFetchedAfterMount, navigate]);

  if (isPending || !isFetchedAfterMount || !campaign)
    return <p className="py-8 text-sm text-muted-foreground">Cargando…</p>;
  if (campaign.status === "draft")
    return (
      <CampaignDraft
        key={campaign.id}
        campaign={campaign}
        onSent={() => refetch()}
      />
    );
  return <CampaignResults campaign={campaign} onChanged={() => refetch()} />;
};

CampaignPage.path = `${EMAIL_PATH}/:id`;
